import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

export function mountScene(host) {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.dataset.motion === 'paused' || new URLSearchParams(location.search).has('no3d')) return;
  let renderer;
  try { renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' }); }
  catch { host.dataset.fallback = 'webgl-unavailable'; return; }
  renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  host.appendChild(renderer.domElement);
  renderer.domElement.setAttribute('aria-hidden', 'true');
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(36, 1, .1, 60);
  camera.position.set(7.3, 5.8, 10.5);
  const target = new THREE.Vector3(0, 1.25, 0);
  const group = new THREE.Group(); scene.add(group);
  const metal = new THREE.MeshStandardMaterial({ color: '#3c4440', metalness: .78, roughness: .3 });
  const dark = new THREE.MeshStandardMaterial({ color: '#131a18', metalness: .6, roughness: .24 });
  const gold = new THREE.MeshStandardMaterial({ color: '#c58b44', metalness: .65, roughness: .28 });
  const glow = new THREE.MeshStandardMaterial({ color: '#f8be64', emissive: '#ee870f', emissiveIntensity: 1.3, metalness: .2, roughness: .3 });
  const cream = new THREE.MeshStandardMaterial({ color: '#e0d7bc', metalness: .1, roughness: .75 });
  const cyan = new THREE.MeshStandardMaterial({ color: '#2dd4bf', emissive: '#0d9488', emissiveIntensity: 1.2, metalness: .3, roughness: .2 });
  const emerald = new THREE.MeshStandardMaterial({ color: '#2ed496', emissive: '#059669', emissiveIntensity: 1.2, metalness: .3, roughness: .2 });
  const box = (w,h,d,x,y,z,mat=metal,parent=group,r=.09) => {
    const m = new THREE.Mesh(new RoundedBoxGeometry(w,h,d,3,r),mat); m.position.set(x,y,z); parent.add(m); return m;
  };
  const line = (points, mat=gold) => {
    const curve = new THREE.CatmullRomCurve3(points.map(p=>new THREE.Vector3(...p)));
    const m = new THREE.Mesh(new THREE.TubeGeometry(curve,40,.012,5,false),mat); group.add(m); return m;
  };
  const textures = [];
  const displays = [];
  function screenTexture(label, kind='pos') {
    const canvas=document.createElement('canvas'); canvas.width=1024; canvas.height=640;
    const c=canvas.getContext('2d'); c.fillStyle='#101913';c.fillRect(0,0,1024,640);
    c.fillStyle='#19281c';c.fillRect(0,0,1024,88);c.fillStyle='#f2cd8b';c.font='bold 29px sans-serif';c.fillText(label,36,54);
    c.fillStyle='#bac8ae';c.font='18px sans-serif';c.fillText('ILLUSTRATIVE DEMO • SYNTHETIC DATA',36,610);
    if(kind==='qr') {
      c.strokeStyle='#e8ddb7';c.lineWidth=12;
      for(const [x,y] of [[220,170],[590,170],[220,380]]){c.strokeRect(x,y,120,120);c.fillStyle='#c7b587';c.fillRect(x+35,y+35,50,50);}
      c.fillStyle='#f3c884';c.font='bold 26px sans-serif';c.fillText('DEMO — NOT SCANNABLE',180,560);
    } else if(kind==='chart') {
      c.fillStyle='#cad4bf'; c.font='24px sans-serif'; c.fillText('Sales / inventory / reporting',36,137);
      for(let i=0;i<9;i++){c.fillStyle=i>5?'#f6b55c':'#657456'; const h=100+((i*73)%260);c.fillRect(55+i*95,510-h,50,h);}
      c.strokeStyle='#44533a';c.lineWidth=2; for(let j=0;j<4;j++){c.beginPath();c.moveTo(40,200+j*90);c.lineTo(970,200+j*90);c.stroke();}
    } else if(kind==='apps-ada') {
      c.fillStyle='#142017';c.fillRect(40,120,420,440);
      c.strokeStyle='#223827';c.lineWidth=18;
      c.beginPath();c.arc(250,310,120,0,Math.PI*2);c.stroke();
      c.strokeStyle='#2ed496';c.lineWidth=18;
      c.beginPath();c.arc(250,310,120,-Math.PI/2,Math.PI*1.45);c.stroke();
      c.fillStyle='#f6efdf';c.font='bold 54px sans-serif';c.fillText('98%',205,320);
      c.fillStyle='#5eead4';c.font='bold 20px sans-serif';c.fillText('WCAG 2.1 AA PASS',145,365);
      const adaStats=[['CONTRAST RATIO','7.2:1 AAA PASS','#5eead4'],['DOM NODES AUDITED','1,842 CLEAN','#f6efdf'],['CRITICAL DEFECTS','0 DETECTED','#2ed496'],['AST REMEDIATION','1 ADVISORY RESOLVED','#f5ae59']];
      adaStats.forEach(([stat,val,col],i)=>{
        c.fillStyle='#19261c';c.fillRect(490,130+i*105,490,85);
        c.fillStyle='#9ca895';c.font='16px sans-serif';c.fillText(stat,515,160+i*105);
        c.fillStyle=col;c.font='bold 22px sans-serif';c.fillText(val,515,195+i*105);
      });
    } else if(kind==='apps-market') {
      c.fillStyle='#141d18';c.fillRect(40,120,940,250);
      c.strokeStyle='#c58b44';c.lineWidth=3;
      c.beginPath();c.moveTo(150,245);c.lineTo(380,190);c.lineTo(650,245);c.stroke();
      c.beginPath();c.moveTo(150,245);c.lineTo(380,300);c.lineTo(650,245);c.stroke();
      c.beginPath();c.moveTo(650,245);c.lineTo(870,245);c.stroke();
      [[150,245,'QUERY','#f5ae59'],[380,190,'10-K','#5eead4'],[380,300,'PATENT','#5eead4'],[650,245,'SYNTH','#f8be64'],[870,245,'DOSSIER','#2ed496']].forEach(([x,y,txt,col])=>{
        c.fillStyle=col;c.beginPath();c.arc(x,y,32,0,Math.PI*2);c.fill();
        c.fillStyle='#0a100d';c.font='bold 15px sans-serif';c.fillText(txt,x-18,y+5);
      });
      const marketStats=[['CONFIDENCE','96% GROUNDED','#2ed496'],['PRIMARY SOURCES','4 CORP FILINGS','#f5ae59'],['HALLUCINATION','< 0.1% FILTERED','#5eead4']];
      marketStats.forEach(([stat,val,col],i)=>{
        c.fillStyle='#19261c';c.fillRect(40+i*325,400,290,140);
        c.fillStyle='#9ca895';c.font='16px sans-serif';c.fillText(stat,65+i*325,445);
        c.fillStyle=col;c.font='bold 24px sans-serif';c.fillText(val,65+i*325,495);
      });
    } else if(kind==='apps-b2b') {
      c.fillStyle='#141e17';c.fillRect(40,120,940,440);
      c.fillStyle='#c58b44';c.fillRect(70,145,260,48);
      c.fillStyle='#0a100d';c.font='bold 20px sans-serif';c.fillText('TARGET: APEX CORP',90,177);
      const b2bRoles=[['VP INFRASTRUCTURE','Marcus Vance · Evaluated POS API','#2ed496'],['DIRECTOR STORE OPS','Elena Rostova · 42 Locations','#5eead4'],['HEAD OF PROCUREMENT','David Chen · Q4 Renewal Target','#f5ae59']];
      b2bRoles.forEach(([role,detail,col],i)=>{
        c.fillStyle='#1e2d22';c.fillRect(70,225+i*105,880,85);
        c.fillStyle=col;c.font='bold 18px sans-serif';c.fillText(role,100,258+i*105);
        c.fillStyle='#c8d4bf';c.font='16px sans-serif';c.fillText(detail,100,290+i*105);
      });
    } else if(kind==='apps-lead') {
      c.fillStyle='#12211c';c.fillRect(40,120,940,440);
      c.fillStyle='#0f3326';c.fillRect(70,140,880,56);
      c.fillStyle='#2ed496';c.font='bold 22px sans-serif';c.fillText('OSINT DOMAIN RECON & MX DELIVERABILITY',95,176);
      const leadRows=[
        ['TARGET DOMAIN','apexlogistics.io · DNS MX Valid','#5eead4'],
        ['PATTERN DETECTED','first.last@apexlogistics.io (94% match)','#2ed496'],
        ['DISCOVERED CONTACTS','Marcus Vance (VP Infra), Elena Rostova (Dir Ops)','#f5ae59'],
        ['API KEYS EXPENDED','$0 (Zero paid keys via public OSINT)','#5eead4']
      ];
      leadRows.forEach(([stat,val,col],i)=>{
        c.fillStyle='#182a22';c.fillRect(70,215+i*80,880,68);
        c.fillStyle='#9ca895';c.font='15px sans-serif';c.fillText(stat,95,242+i*80);
        c.fillStyle=col;c.font='bold 19px sans-serif';c.fillText(val,95,268+i*80);
      });
    } else if(kind==='apps-video') {
      c.fillStyle='#1a151f';c.fillRect(40,120,940,440);
      c.fillStyle='#2b1a33';c.fillRect(70,140,880,56);
      c.fillStyle='#f5ae59';c.font='bold 22px sans-serif';c.fillText('VIRAL VIDEO STUDIO · VERTICAL 9:16 HD',95,176);
      c.fillStyle='#140e1a';c.fillRect(70,215,380,305);
      c.strokeStyle='#f5ae59';c.lineWidth=3;c.strokeRect(70,215,380,305);
      c.fillStyle='#f8be64';c.beginPath();c.arc(260,340,36,0,Math.PI*2);c.fill();
      c.fillStyle='#140e1a';c.beginPath();c.moveTo(252,324);c.lineTo(276,340);c.lineTo(252,356);c.fill();
      c.fillStyle='#f6efdf';c.font='bold 16px sans-serif';c.fillText('00:04.2 / 00:06.0',200,410);
      const videoStats=[
        ['FORMAT','Finished 4–8s Vertical (9:16) MP4','#5eead4'],
        ['ONE-CHANGE REMIX','Product Swap / Background Anchor','#f5ae59'],
        ['DISTRIBUTION','TikTok · Reels · Shorts Ready','#2ed496']
      ];
      videoStats.forEach(([stat,val,col],i)=>{
        c.fillStyle='#25182e';c.fillRect(480,215+i*100,470,82);
        c.fillStyle='#ba9ec4';c.font='15px sans-serif';c.fillText(stat,505,246+i*100);
        c.fillStyle=col;c.font='bold 20px sans-serif';c.fillText(val,505,278+i*100);
      });
    } else if(kind==='apps-hook') {
      c.fillStyle='#1b1824';c.fillRect(40,120,940,440);
      c.fillStyle='#2c2238';c.fillRect(70,140,880,56);
      c.fillStyle='#c084fc';c.font='bold 22px sans-serif';c.fillText('FIRST 6-SECOND RETENTION AUDIT',95,176);
      c.strokeStyle='#f8be64';c.lineWidth=5;
      c.beginPath();c.moveTo(90,320);c.bezierCurveTo(240,335,380,370,550,400);c.stroke();
      [[90,320,'0s: 100%'],[240,335,'2s: 91%'],[380,370,'4s: 84%'],[550,400,'6s: 78%']].forEach(([x,y,txt])=>{
        c.fillStyle='#f8be64';c.beginPath();c.arc(x,y,7,0,Math.PI*2);c.fill();
        c.fillStyle='#f6efdf';c.font='bold 15px sans-serif';c.fillText(txt,x-24,y-14);
      });
      const hookStats=[
        ['HOOK SCORE','91 / 100 (High Retention)','#2ed496'],
        ['5-FACTOR RUBRIC','Pattern: 9.4 · Clarity: 9.1 · Payoff: 8.8','#f5ae59'],
        ['PRESCRIPTION','Opening-line rewrite ready (+28% lift)','#5eead4']
      ];
      hookStats.forEach(([stat,val,col],i)=>{
        c.fillStyle='#251d30';c.fillRect(590,215+i*95,360,80);
        c.fillStyle='#b8a9c9';c.font='14px sans-serif';c.fillText(stat,610,244+i*95);
        c.fillStyle=col;c.font='bold 17px sans-serif';c.fillText(val,610,274+i*95);
      });
    } else {
      const labels=['Canvas tote','Ceramic mug','Desk lamp','Notebook','Gift wrap','Service'];
      labels.forEach((t,i)=>{const x=30+(i%3)*208,y=122+Math.floor(i/3)*205;c.fillStyle='#283b28';c.fillRect(x,y,187,177);c.fillStyle=['#d1ad72','#9bafa0','#be9c6d'][i%3];c.fillRect(x+53,y+22,75,65);c.fillStyle='#ecebd9';c.font='18px sans-serif';c.fillText(t,x+15,y+137);});
      c.fillStyle='#233224';c.fillRect(683,120,307,430);c.fillStyle='#eed6a8';c.font='bold 24px sans-serif';c.fillText('Current order',710,165);
      ['Ceramic mug × 1','Notebook × 2','Review order'].forEach((t,i)=>{c.font='20px sans-serif';c.fillText(t,710,228+i*60);});
      c.fillStyle='#ecc184';c.fillRect(710,450,245,60);c.fillStyle='#1d2315';c.font='bold 21px sans-serif';c.fillText('Demo checkout',738,490);
    }
    const texture=new THREE.CanvasTexture(canvas);texture.colorSpace=THREE.SRGBColorSpace;textures.push(texture);return texture;
  }
  function display(w,h,x,y,z,label,kind='pos',rot=0) {
    const g=new THREE.Group();g.position.set(x,y,z);g.rotation.y=rot;group.add(g);
    box(w+.12,h+.12,.18,0,0,0,dark,g);
    const p=new THREE.Mesh(new THREE.PlaneGeometry(w,h),new THREE.MeshBasicMaterial({map:screenTexture(label,kind)}));p.position.z=.101;g.add(p);
    displays.push(g);
    return g;
  }
  const mode=host.dataset.scene||'pos';
  let updateContinuous = null;

  if(['pos','journey','auth'].includes(mode)) {
    box(2.2,.12,1.35,-.4,.04,.1,metal);
    const stand=box(.37,1.38,.42,-.4,.73,-.06,metal);stand.rotation.x=-.2;
    const terminal=display(3.35,2.13,-.4,2.17,0,'POINT OF SALE','pos',-.12);terminal.rotation.x=-.16;
    box(1.28,.12,.94,1.85,.06,1.2,dark);
    const reader=display(.9,1.12,1.85,.85,1.2,'PAYMENT','pos',-.3);reader.rotation.x=-.3;
    box(.76,.14,.48,1.85,.2,1.45,gold);
    display(1.1,1.73,-2.55,1.2,.95,'ORDERING','qr',.3);
    box(1.3,.08,.6,-2.55,.08,1.05,gold);
    const tablet=display(1.9,1.35,2.2,1.2,-1.55,'INVENTORY','chart',-.45);tablet.rotation.x=-.12;
    display(1.95,1.23,-2.1,3.5,-1.8,'REPORTING','chart',.2);
    line([[-2.4,.1,1.1],[-2.4,.13,2.1],[.1,.13,2.6],[2.2,.13,1.3]],glow);
    line([[-.2,.1,-.4],[.6,.13,-2.1],[2,.13,-1.6]],gold);
    updateContinuous = (dt) => {
      baseRotY += dt * 0.04;
    };
  } else if(mode==='commerce') {
    box(2.2,1.9,1.7,-.7,1,0,cream);box(.15,1.94,1.74,-.7,1,0,gold);
    box(1.6,1.35,1.35,1.4,.75,.8,metal);box(1.64,.08,1.4,1.4,1.3,.8,gold);
    const ring=new THREE.Mesh(new THREE.TorusGeometry(.65,.095,16,48),gold);ring.position.set(1.4,2.8,.3);ring.rotation.x=.5;group.add(ring);
    const band=new THREE.Mesh(new THREE.TorusGeometry(.66,.13,16,48,Math.PI),dark);band.position.set(-2.2,2.7,.9);group.add(band);
    box(.4,.8,.45,-2.86,2.4,.9,metal);box(.4,.8,.45,-1.54,2.4,.9,metal);
    display(1.4,1.9,.3,3,-1.2,'CATALOG','pos',-.25);
    line([[-2,.1,1.7],[0,.1,2.1],[2.6,.1,.4]],glow);
    updateContinuous = (dt) => {
      baseRotY += dt * 0.05;
      ring.rotation.y += dt * 0.35;
    };
  } else if(mode==='apps') {
    // 1. MAIN APPS HUB: Concentric orbital rings, central floating crystal, 4 orbiting data cards
    const ring1 = new THREE.Mesh(new THREE.TorusGeometry(3.6, .016, 8, 96), glow);
    ring1.rotation.x = 1.2; ring1.rotation.y = .25; group.add(ring1);
    
    const ring2 = new THREE.Mesh(new THREE.TorusGeometry(2.7, .012, 8, 80), gold);
    ring2.rotation.x = .8; ring2.rotation.z = -.35; group.add(ring2);

    const ring3 = new THREE.Mesh(new THREE.TorusGeometry(4.2, .009, 6, 96), cyan);
    ring3.rotation.x = 1.45; ring3.rotation.y = -.2; group.add(ring3);

    // Floating core crystal
    const core = new THREE.Mesh(new THREE.IcosahedronGeometry(.5, 0), glow);
    core.position.set(0, 1.5, 0); group.add(core);
    const coreWire = new THREE.Mesh(new THREE.IcosahedronGeometry(.68, 1), new THREE.MeshBasicMaterial({ color: '#f8be64', wireframe: true, transparent: true, opacity: .45 }));
    coreWire.position.set(0, 1.5, 0); group.add(coreWire);

    // 4 Floating Crystalline Glass Data Cards
    const card1 = display(1.7, 1.22, -1.8, 2.1, .75, 'ADA COMPLIANCE', 'apps-ada', .22);
    const card2 = display(1.85, 1.3, 1.7, 2.2, .45, 'DEEP MARKET INTEL', 'apps-market', -.28);
    const card3 = display(1.6, 1.15, -1.2, .8, -.65, 'B2B ACCOUNT INTEL', 'apps-b2b', .16);
    const card4 = display(1.75, 1.25, 1.3, .7, -.75, 'AITRADE ENGINE', 'chart', -.2);

    // Glowing connection curves
    line([[-1.8, 2.1, .75], [0, 2.3, .2], [1.7, 2.2, .45]], glow);
    line([[-1.2, .8, -.65], [0, .9, -.4], [1.3, .7, -.75]], cyan);
    line([[-1.8, 2.1, .75], [-1.2, .8, -.65]], gold);
    line([[1.7, 2.2, .45], [1.3, .7, -.75]], gold);

    // Dynamic particle constellation
    const particleGeo = new THREE.BufferGeometry();
    const particleCount = 280;
    const posArray = new Float32Array(particleCount * 3);
    for(let i=0; i<particleCount*3; i+=3) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      const r = 1.3 + Math.random() * 3.5;
      posArray[i] = r * Math.sin(phi) * Math.cos(theta);
      posArray[i+1] = (r * Math.sin(phi) * Math.sin(theta)) * .65 + 1.3;
      posArray[i+2] = r * Math.cos(phi);
    }
    particleGeo.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
    const particleMat = new THREE.PointsMaterial({
      size: .055,
      color: '#f8be64',
      transparent: true,
      opacity: .8,
      blending: THREE.AdditiveBlending,
    });
    const particles = new THREE.Points(particleGeo, particleMat);
    group.add(particles);

    updateContinuous = (dt, animTime) => {
      baseRotY += dt * 0.18;
      ring1.rotation.z += dt * 0.35;
      ring1.rotation.x += dt * 0.12;
      ring2.rotation.y += dt * 0.28;
      ring2.rotation.z -= dt * 0.15;
      ring3.rotation.z -= dt * 0.22;
      core.position.y = 1.5 + Math.sin(animTime * 1.8) * 0.08;
      core.rotation.y += dt * 0.55;
      core.rotation.x += dt * 0.28;
      coreWire.position.y = core.position.y;
      coreWire.rotation.y -= dt * 0.38;
      particles.rotation.y += dt * 0.08;
      card1.position.y = 2.1 + Math.sin(animTime * 1.4) * 0.06;
      card2.position.y = 2.2 + Math.cos(animTime * 1.5) * 0.06;
      card3.position.y = 0.8 + Math.sin(animTime * 1.6 + 1) * 0.05;
      card4.position.y = 0.7 + Math.cos(animTime * 1.3 + 1) * 0.05;
    };
  } else if(mode==='apps-ada') {
    // 2. ADA COMPLIANCE: 3D tablet with wireframe DOM, sweeping scanner beam, WCAG rings, ascending particles
    box(3.2, 4.0, .14, 0, 1.85, 0, dark);
    box(3.28, 4.08, .08, 0, 1.85, -.03, metal);
    display(2.8, 3.4, 0, 1.85, .08, 'ACCESSIBILITY AUDIT', 'apps-ada', 0);

    // Wireframe DOM structure nodes layered on top
    const domGroup = new THREE.Group(); domGroup.position.set(0, 1.85, .15); group.add(domGroup);
    box(2.4, .28, .06, 0, 1.35, 0, cyan, domGroup);
    for(let i=0; i<3; i++) {
      box(.55, .14, .04, -.65 + i * .65, 1.0, 0, metal, domGroup);
    }
    const domOutline = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(2.4, 1.8, .06)),
      new THREE.LineBasicMaterial({ color: '#2ed496', transparent: true, opacity: .75 })
    );
    domOutline.position.set(0, -.1, 0); domGroup.add(domOutline);
    box(1.1, .28, .08, 0, -1.2, 0, emerald, domGroup);

    // Sweeping holographic scanner bar
    const scannerGroup = new THREE.Group(); scannerGroup.position.set(0, 1.85, .22); group.add(scannerGroup);
    box(2.9, .05, .14, 0, 0, 0, emerald, scannerGroup);
    const scannerGlow = new THREE.Mesh(
      new THREE.PlaneGeometry(2.9, .32),
      new THREE.MeshBasicMaterial({ color: '#2ed496', transparent: true, opacity: .35, side: THREE.DoubleSide })
    );
    scannerGroup.add(scannerGlow);

    // Floating orbital rings with WCAG badges
    const adaRing1 = new THREE.Mesh(new THREE.TorusGeometry(3.1, .016, 8, 80), emerald);
    adaRing1.rotation.x = 1.25; adaRing1.rotation.y = .3; group.add(adaRing1);
    const adaRing2 = new THREE.Mesh(new THREE.TorusGeometry(2.5, .012, 8, 80), cyan);
    adaRing2.rotation.x = .75; adaRing2.rotation.z = -.4; group.add(adaRing2);

    // Floating telemetry badges
    const badge1 = display(1.5, 1.05, -2.1, 2.5, .7, 'WCAG 2.1 AA', 'apps-ada', .28);
    const badge2 = display(1.4, .95, 2.0, 1.1, .6, 'CONTRAST RATIO', 'apps-ada', -.3);

    // Floating connection curves
    line([[-2.1, 2.5, .7], [0, 2.8, .2], [2.0, 1.1, .6]], emerald);

    // Ascending emerald & cyan telemetry particles
    const adaCount = 240;
    const adaParticleGeo = new THREE.BufferGeometry();
    const adaPos = new Float32Array(adaCount * 3);
    for(let i=0; i<adaCount*3; i+=3) {
      adaPos[i] = (Math.random() - 0.5) * 4.6;
      adaPos[i+1] = Math.random() * 4.2;
      adaPos[i+2] = (Math.random() - 0.5) * 3.6;
    }
    adaParticleGeo.setAttribute('position', new THREE.BufferAttribute(adaPos, 3));
    const adaParticles = new THREE.Points(adaParticleGeo, new THREE.PointsMaterial({
      size: .058,
      color: '#2ed496',
      transparent: true,
      opacity: .85,
      blending: THREE.AdditiveBlending
    }));
    group.add(adaParticles);

    updateContinuous = (dt, animTime) => {
      baseRotY += dt * 0.16;
      const scanY = 1.85 + Math.sin(animTime * 2.2) * 1.35;
      scannerGroup.position.y = scanY;
      adaRing1.rotation.z += dt * 0.32;
      adaRing1.rotation.x += dt * 0.1;
      adaRing2.rotation.y += dt * 0.35;
      adaRing2.rotation.z -= dt * 0.15;
      domGroup.position.y = 1.85 + Math.sin(animTime * 1.5) * 0.04;
      badge1.position.y = 2.5 + Math.sin(animTime * 1.7) * 0.06;
      badge2.position.y = 1.1 + Math.cos(animTime * 1.6) * 0.05;
      for(let i=1; i<adaCount*3; i+=3) {
        adaPos[i] += dt * 0.85;
        if (adaPos[i] > 4.2) adaPos[i] = 0;
      }
      adaParticleGeo.attributes.position.needsUpdate = true;
      adaParticles.rotation.y += dt * 0.08;
    };
  } else if(mode==='apps-market') {
    // 3. DEEP MARKET INTEL: Rotating geodesic wireframe globe, inner core, 4 orbital satellites, golden galaxy
    const globeWire = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.65, 2),
      new THREE.MeshBasicMaterial({ color: '#f5ae59', wireframe: true, transparent: true, opacity: .55 })
    );
    globeWire.position.set(0, 1.8, 0); group.add(globeWire);
    const globeCore = new THREE.Mesh(new THREE.IcosahedronGeometry(.82, 1), glow);
    globeCore.position.set(0, 1.8, 0); group.add(globeCore);

    // Geodesic latitude and longitude orbital rings
    const latRing = new THREE.Mesh(new THREE.TorusGeometry(1.68, .014, 6, 80), gold);
    latRing.rotation.x = Math.PI / 2; latRing.position.set(0, 1.8, 0); group.add(latRing);
    const longRing = new THREE.Mesh(new THREE.TorusGeometry(1.68, .014, 6, 80), glow);
    longRing.position.set(0, 1.8, 0); group.add(longRing);
    const polarRing = new THREE.Mesh(new THREE.TorusGeometry(3.4, .012, 6, 96), gold);
    polarRing.rotation.x = 1.15; polarRing.rotation.y = .35; group.add(polarRing);

    // 4 Orbital Satellite Citation Nodes (SEC, PATENTS, TRANSCRIPTS, REGS)
    const makeSatellite = (col) => {
      const satGroup = new THREE.Group();
      const node = new THREE.Mesh(new THREE.OctahedronGeometry(.24, 0), col);
      satGroup.add(node);
      const wire = new THREE.Mesh(new THREE.OctahedronGeometry(.36, 0), new THREE.MeshBasicMaterial({ color: col.color, wireframe: true, transparent: true, opacity: .6 }));
      satGroup.add(wire);
      group.add(satGroup);
      return satGroup;
    };
    const sat1 = makeSatellite(glow);
    const sat2 = makeSatellite(cyan);
    const sat3 = makeSatellite(gold);
    const sat4 = makeSatellite(glow);

    // Floating Market Intel Citation Cards
    const mCard1 = display(1.85, 1.25, -2.1, 2.4, .65, 'SEC 10-K & PATENTS', 'apps-market', .25);
    const mCard2 = display(1.75, 1.15, 2.0, 1.25, .6, 'GROUNDED CLAIMS', 'apps-market', -.3);

    // Glowing connection curve between citations and center
    line([[-2.1, 2.4, .65], [0, 2.7, .1], [2.0, 1.25, .6]], glow);

    // Expansive golden galaxy particle field
    const marketCount = 300;
    const marketParticleGeo = new THREE.BufferGeometry();
    const marketPos = new Float32Array(marketCount * 3);
    for(let i=0; i<marketCount*3; i+=3) {
      const theta = Math.random() * Math.PI * 2;
      const r = 1.2 + Math.random() * 3.6;
      marketPos[i] = r * Math.cos(theta);
      marketPos[i+1] = 1.8 + (Math.random() - 0.5) * 1.8 * (1 - r / 5);
      marketPos[i+2] = r * Math.sin(theta);
    }
    marketParticleGeo.setAttribute('position', new THREE.BufferAttribute(marketPos, 3));
    const marketParticles = new THREE.Points(marketParticleGeo, new THREE.PointsMaterial({
      size: .054,
      color: '#f8be64',
      transparent: true,
      opacity: .8,
      blending: THREE.AdditiveBlending
    }));
    group.add(marketParticles);

    updateContinuous = (dt, animTime) => {
      baseRotY += dt * 0.18;
      globeWire.rotation.y += dt * 0.35;
      globeWire.rotation.x += dt * 0.14;
      globeCore.rotation.y -= dt * 0.45;
      latRing.rotation.z += dt * 0.22;
      longRing.rotation.y += dt * 0.28;
      polarRing.rotation.z -= dt * 0.15;
      sat1.position.set(Math.cos(animTime * 0.75) * 2.7, 1.8 + Math.sin(animTime * 0.75) * 0.7, Math.sin(animTime * 0.75) * 2.7);
      sat1.rotation.y += dt * 2.0;
      sat2.position.set(Math.cos(animTime * 0.6 + 1.6) * 2.5, 1.8 + Math.sin(animTime * 0.8) * 0.8, Math.sin(animTime * 0.6 + 1.6) * 2.5);
      sat2.rotation.x += dt * 1.8;
      sat3.position.set(Math.cos(animTime * 0.68 + 3.2) * 2.9, 1.8 + Math.cos(animTime * 0.6) * 0.6, Math.sin(animTime * 0.68 + 3.2) * 2.9);
      sat3.rotation.z += dt * 1.6;
      sat4.position.set(Math.cos(animTime * 0.82 + 4.8) * 2.3, 1.8 + Math.sin(animTime * 0.95) * 0.5, Math.sin(animTime * 0.82 + 4.8) * 2.3);
      sat4.rotation.y -= dt * 1.9;
      mCard1.position.y = 2.4 + Math.sin(animTime * 1.4) * 0.06;
      mCard2.position.y = 1.25 + Math.cos(animTime * 1.5) * 0.05;
      marketParticles.rotation.y += dt * 0.12;
    };
  } else if(mode==='apps-b2b') {
    // 4. B2B ACCOUNT INTEL: Tiered hexagonal organization pedestals, laser links, radar sweep, orbiting tech cubes
    const topHex = new THREE.Mesh(new THREE.CylinderGeometry(.65, .72, .24, 6), dark);
    topHex.position.set(0, 2.5, 0); group.add(topHex);
    const topRim = new THREE.Mesh(new THREE.TorusGeometry(.68, .016, 6, 6), glow);
    topRim.rotation.x = Math.PI / 2; topRim.position.set(0, 2.62, 0); group.add(topRim);
    const topNode = new THREE.Mesh(new THREE.OctahedronGeometry(.36, 0), glow);
    topNode.position.set(0, 2.95, 0); group.add(topNode);

    const makeVPPedestal = (x, z) => {
      const hex = new THREE.Mesh(new THREE.CylinderGeometry(.52, .58, .2, 6), dark);
      hex.position.set(x, 1.65, z); group.add(hex);
      const rim = new THREE.Mesh(new THREE.TorusGeometry(.55, .014, 6, 6), cyan);
      rim.rotation.x = Math.PI / 2; rim.position.set(x, 1.75, z); group.add(rim);
      const node = new THREE.Mesh(new THREE.OctahedronGeometry(.26, 0), cyan);
      node.position.set(x, 2.05, z); group.add(node);
      return { hex, rim, node };
    };
    const vp1 = makeVPPedestal(-1.5, .4);
    const vp2 = makeVPPedestal(1.5, .4);

    const makeDirectorPedestal = (x, z) => {
      const hex = new THREE.Mesh(new THREE.CylinderGeometry(.45, .5, .18, 6), dark);
      hex.position.set(x, .85, z); group.add(hex);
      const rim = new THREE.Mesh(new THREE.TorusGeometry(.48, .012, 6, 6), gold);
      rim.rotation.x = Math.PI / 2; rim.position.set(x, .94, z); group.add(rim);
      const node = new THREE.Mesh(new THREE.OctahedronGeometry(.22, 0), gold);
      node.position.set(x, 1.2, z); group.add(node);
      return { hex, rim, node };
    };
    const dir1 = makeDirectorPedestal(-1.0, 1.3);
    const dir2 = makeDirectorPedestal(1.0, 1.3);

    // Laser connection lines (Org Chart Hierarchy)
    line([[0, 2.95, 0], [-1.5, 2.05, .4]], glow);
    line([[0, 2.95, 0], [1.5, 2.05, .4]], glow);
    line([[-1.5, 2.05, .4], [-1.0, 1.2, 1.3]], cyan);
    line([[1.5, 2.05, .4], [1.0, 1.2, 1.3]], cyan);

    // Sweeping 3D Radar Circle & Sweeping Radar Beam
    const radarRing = new THREE.Mesh(new THREE.TorusGeometry(3.3, .016, 8, 96), cyan);
    radarRing.rotation.x = Math.PI / 2; radarRing.position.y = .05; group.add(radarRing);
    const innerRadarRing = new THREE.Mesh(new THREE.TorusGeometry(2.1, .012, 8, 80), gold);
    innerRadarRing.rotation.x = Math.PI / 2; innerRadarRing.position.y = .06; group.add(innerRadarRing);

    const radarSweep = new THREE.Group(); radarSweep.position.y = .07; group.add(radarSweep);
    const beamMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(3.3, .08),
      new THREE.MeshBasicMaterial({ color: '#2dd4bf', transparent: true, opacity: .9, side: THREE.DoubleSide })
    );
    beamMesh.position.x = 1.65; beamMesh.rotation.x = Math.PI / 2; radarSweep.add(beamMesh);
    const fanMesh = new THREE.Mesh(
      new THREE.CircleGeometry(3.3, 24, 0, Math.PI / 4),
      new THREE.MeshBasicMaterial({ color: '#2dd4bf', transparent: true, opacity: .2, side: THREE.DoubleSide })
    );
    fanMesh.rotation.x = Math.PI / 2; radarSweep.add(fanMesh);

    // Floating Orbiting Tech Stack Cubes (AWS, PG, KAFKA)
    const techGroup = new THREE.Group(); group.add(techGroup);
    const cube1 = box(.36, .36, .36, 2.3, 1.9, 0, metal, techGroup);
    const cube2 = box(.36, .36, .36, -2.2, 1.3, .6, cyan, techGroup);
    const cube3 = box(.36, .36, .36, 0, 1.1, -2.2, gold, techGroup);

    // Floating Dossier Cards
    const b2bCard1 = display(1.85, 1.25, 2.0, 2.3, .4, 'APEX CORP DOSSIER', 'apps-b2b', -.32);
    const b2bCard2 = display(1.65, 1.1, -1.9, 1.1, .6, 'BUYING INTENT RADAR', 'apps-b2b', .28);

    updateContinuous = (dt, animTime) => {
      baseRotY += dt * 0.16;
      radarSweep.rotation.y += dt * 1.8;
      techGroup.rotation.y -= dt * 0.35;
      cube1.rotation.y += dt * 1.2; cube1.rotation.x += dt * 0.8;
      cube2.rotation.y += dt * 1.0; cube2.rotation.z += dt * 0.7;
      cube3.rotation.x += dt * 1.1; cube3.rotation.z += dt * 0.9;
      topNode.position.y = 2.95 + Math.sin(animTime * 2.0) * 0.05;
      topNode.rotation.y += dt * 1.2;
      vp1.node.rotation.y += dt * 1.0;
      vp2.node.rotation.y -= dt * 1.0;
      dir1.node.rotation.y += dt * 0.8;
      dir2.node.rotation.y -= dt * 0.8;
      b2bCard1.position.y = 2.3 + Math.sin(animTime * 1.4) * 0.05;
      b2bCard2.position.y = 1.1 + Math.cos(animTime * 1.5) * 0.05;
    };
  } else if(mode==='apps-lead') {
    // 5. B2B LEAD ENRICHMENT: DNS MX matrix, router core, orbiting pattern cubes, cyber matrix particle stream
    box(2.4, 2.8, 1.2, 0, 1.8, 0, dark);
    const serverRim = new THREE.Mesh(new THREE.TorusGeometry(1.5, .016, 8, 72), emerald);
    serverRim.rotation.x = Math.PI / 2; serverRim.position.set(0, 1.8, 0); group.add(serverRim);

    display(2.2, 1.35, 0, 2.2, .65, 'LEAD ENRICHMENT', 'apps-lead', 0);

    const makePatternCube = (x, z, col) => {
      const g = new THREE.Group(); g.position.set(x, 1.3, z);
      box(.7, .5, .5, 0, 0, 0, dark, g);
      const wire = new THREE.Mesh(new THREE.BoxGeometry(.72, .52, .52), new THREE.MeshBasicMaterial({ color: col.color, wireframe: true }));
      g.add(wire);
      group.add(g);
      return g;
    };
    const cube1 = makePatternCube(-2.2, .8, emerald);
    const cube2 = makePatternCube(2.2, .6, cyan);
    const cube3 = makePatternCube(0, -2.1, gold);

    const leadRing1 = new THREE.Mesh(new THREE.TorusGeometry(3.2, .014, 8, 96), cyan);
    leadRing1.rotation.x = 1.3; leadRing1.rotation.y = .2; group.add(leadRing1);
    const leadRing2 = new THREE.Mesh(new THREE.TorusGeometry(2.5, .012, 8, 80), emerald);
    leadRing2.rotation.x = .8; leadRing2.rotation.z = -.3; group.add(leadRing2);

    line([[-2.2, 1.3, .8], [0, 1.8, 0], [2.2, 1.3, .6]], emerald);

    const leadCount = 260;
    const leadGeo = new THREE.BufferGeometry();
    const leadPos = new Float32Array(leadCount * 3);
    for(let i=0; i<leadCount*3; i+=3) {
      leadPos[i] = (Math.random() - 0.5) * 4.4;
      leadPos[i+1] = Math.random() * 4.0;
      leadPos[i+2] = (Math.random() - 0.5) * 4.0;
    }
    leadGeo.setAttribute('position', new THREE.BufferAttribute(leadPos, 3));
    const leadParticles = new THREE.Points(leadGeo, new THREE.PointsMaterial({
      size: .055,
      color: '#2ed496',
      transparent: true,
      opacity: .85,
      blending: THREE.AdditiveBlending
    }));
    group.add(leadParticles);

    updateContinuous = (dt, animTime) => {
      baseRotY += dt * 0.16;
      leadRing1.rotation.z += dt * 0.3;
      leadRing2.rotation.y += dt * 0.35;
      cube1.position.set(Math.cos(animTime * 0.8) * 2.4, 1.3 + Math.sin(animTime * 1.5) * 0.2, Math.sin(animTime * 0.8) * 2.4);
      cube1.rotation.y += dt * 1.5;
      cube2.position.set(Math.cos(animTime * 0.7 + 2.1) * 2.5, 1.3 + Math.cos(animTime * 1.3) * 0.2, Math.sin(animTime * 0.7 + 2.1) * 2.5);
      cube2.rotation.x += dt * 1.2;
      cube3.position.set(Math.cos(animTime * 0.75 + 4.2) * 2.3, 1.3 + Math.sin(animTime * 1.1) * 0.15, Math.sin(animTime * 0.75 + 4.2) * 2.3);
      cube3.rotation.z += dt * 1.4;
      for(let i=1; i<leadCount*3; i+=3) {
        leadPos[i] -= dt * 0.9;
        if(leadPos[i] < 0) leadPos[i] = 4.0;
      }
      leadGeo.attributes.position.needsUpdate = true;
    };
  } else if(mode==='apps-video') {
    // 6. VIRAL VIDEO STUDIO: 9:16 vertical smartphone screen, lens rings, orbiting remix cubes, cinematic sparkles
    box(2.2, 4.0, .14, 0, 1.85, 0, dark);
    box(2.28, 4.08, .08, 0, 1.85, -.03, metal);
    display(2.0, 3.6, 0, 1.85, .08, 'VIRAL VIDEO STUDIO', 'apps-video', 0);

    const apertureRing1 = new THREE.Mesh(new THREE.TorusGeometry(1.6, .018, 8, 80), gold);
    apertureRing1.position.set(0, 1.85, .16); group.add(apertureRing1);
    const apertureRing2 = new THREE.Mesh(new THREE.TorusGeometry(2.8, .014, 8, 96), glow);
    apertureRing2.rotation.x = 1.2; apertureRing2.rotation.y = .3; group.add(apertureRing2);

    const makeRemixCube = (x, z, col) => {
      const g = new THREE.Group(); g.position.set(x, 1.85, z);
      box(.55, .55, .55, 0, 0, 0, dark, g);
      const wire = new THREE.Mesh(new THREE.BoxGeometry(.58, .58, .58), new THREE.MeshBasicMaterial({ color: col.color, wireframe: true }));
      g.add(wire);
      group.add(g);
      return g;
    };
    const rCube1 = makeRemixCube(-2.1, .7, glow);
    const rCube2 = makeRemixCube(2.1, .7, gold);
    const rCube3 = makeRemixCube(0, -1.9, cyan);

    line([[-2.1, 1.85, .7], [0, 2.4, .2], [2.1, 1.85, .7]], glow);

    const videoCount = 280;
    const videoGeo = new THREE.BufferGeometry();
    const videoPos = new Float32Array(videoCount * 3);
    for(let i=0; i<videoCount*3; i+=3) {
      const theta = Math.random() * Math.PI * 2;
      const r = 1.2 + Math.random() * 3.2;
      videoPos[i] = r * Math.cos(theta);
      videoPos[i+1] = 1.85 + (Math.random() - 0.5) * 3.4;
      videoPos[i+2] = r * Math.sin(theta);
    }
    videoGeo.setAttribute('position', new THREE.BufferAttribute(videoPos, 3));
    const videoParticles = new THREE.Points(videoGeo, new THREE.PointsMaterial({
      size: .058,
      color: '#f8be64',
      transparent: true,
      opacity: .85,
      blending: THREE.AdditiveBlending
    }));
    group.add(videoParticles);

    updateContinuous = (dt, animTime) => {
      baseRotY += dt * 0.16;
      apertureRing1.rotation.z += dt * 0.5;
      apertureRing2.rotation.z -= dt * 0.25;
      apertureRing2.rotation.x += dt * 0.1;
      rCube1.position.set(Math.cos(animTime * 0.75) * 2.5, 1.85 + Math.sin(animTime * 1.4) * 0.25, Math.sin(animTime * 0.75) * 2.5);
      rCube1.rotation.y += dt * 1.8;
      rCube2.position.set(Math.cos(animTime * 0.65 + 2.2) * 2.4, 1.85 + Math.cos(animTime * 1.2) * 0.25, Math.sin(animTime * 0.65 + 2.2) * 2.4);
      rCube2.rotation.x += dt * 1.6;
      rCube3.position.set(Math.cos(animTime * 0.8 + 4.4) * 2.2, 1.85 + Math.sin(animTime * 1.0) * 0.2, Math.sin(animTime * 0.8 + 4.4) * 2.2);
      rCube3.rotation.z += dt * 1.5;
      videoParticles.rotation.y += dt * 0.1;
    };
  } else if(mode==='apps-hook') {
    // 7. HOOK LENS: 3D retention trajectory curve, optical iris rings, 5-factor rubric markers, focused light beam
    const lensRing1 = new THREE.Mesh(new THREE.TorusGeometry(1.8, .02, 8, 80), glow);
    lensRing1.position.set(0, 1.8, 0); group.add(lensRing1);
    const lensRing2 = new THREE.Mesh(new THREE.TorusGeometry(2.6, .015, 8, 96), cyan);
    lensRing2.position.set(0, 1.8, 0); lensRing2.rotation.x = .9; group.add(lensRing2);
    const lensRing3 = new THREE.Mesh(new THREE.TorusGeometry(3.3, .012, 8, 96), gold);
    lensRing3.position.set(0, 1.8, 0); lensRing3.rotation.y = .7; group.add(lensRing3);

    line([[-2.4, 2.7, -1.2], [-1.2, 2.4, -.4], [0, 2.0, .3], [1.2, 1.6, .8], [2.4, 1.2, 1.3]], glow);

    display(2.3, 1.45, 0, 1.75, .35, 'HOOK RETENTION AUDIT', 'apps-hook', 0);

    const makeRubricBeacon = (x, y, z, col) => {
      const g = new THREE.Group(); g.position.set(x, y, z);
      const beacon = new THREE.Mesh(new THREE.OctahedronGeometry(.2, 0), col);
      g.add(beacon);
      group.add(g);
      return g;
    };
    const b1 = makeRubricBeacon(-2.4, 2.7, -1.2, glow);
    const b2 = makeRubricBeacon(-1.2, 2.4, -.4, cyan);
    const b3 = makeRubricBeacon(0, 2.0, .3, emerald);
    const b4 = makeRubricBeacon(1.2, 1.6, .8, gold);
    const b5 = makeRubricBeacon(2.4, 1.2, 1.3, glow);

    const hookCount = 260;
    const hookGeo = new THREE.BufferGeometry();
    const hookPos = new Float32Array(hookCount * 3);
    for(let i=0; i<hookCount*3; i+=3) {
      const theta = Math.random() * Math.PI * 2;
      const r = 1.3 + Math.random() * 3.3;
      hookPos[i] = r * Math.cos(theta);
      hookPos[i+1] = 1.8 + (Math.random() - 0.5) * 2.8;
      hookPos[i+2] = r * Math.sin(theta);
    }
    hookGeo.setAttribute('position', new THREE.BufferAttribute(hookPos, 3));
    const hookParticles = new THREE.Points(hookGeo, new THREE.PointsMaterial({
      size: .054,
      color: '#f8be64',
      transparent: true,
      opacity: .85,
      blending: THREE.AdditiveBlending
    }));
    group.add(hookParticles);

    updateContinuous = (dt, animTime) => {
      baseRotY += dt * 0.16;
      lensRing1.rotation.z += dt * 0.45;
      lensRing2.rotation.z -= dt * 0.3;
      lensRing2.rotation.y += dt * 0.15;
      lensRing3.rotation.x += dt * 0.25;
      b1.rotation.y += dt * 1.5;
      b2.rotation.y += dt * 1.5;
      b3.rotation.y += dt * 1.5;
      b4.rotation.y += dt * 1.5;
      b5.rotation.y += dt * 1.5;
      hookParticles.rotation.y += dt * 0.08;
    };
  } else {
    for(let i=0;i<3;i++){
      const panel=display(2.1,2.8,(i-1)*1.65,1.8+i*.35,-i*.7,mode==='analytics'?['RESEARCH','SIGNAL REVIEW','RISK CONTEXT'][i]:['THE OPERATOR','FIELD NOTES','IDEAS TO ACTION'][i],mode==='analytics'?'chart':'pos',-.3+i*.08);
      box(2.25,.05,.05,(i-1)*1.65,.38+i*.35,.1-i*.7,glow);
    }
    updateContinuous = (dt) => {
      baseRotY += dt * 0.04;
    };
  }

  const platform=new THREE.Mesh(new THREE.CylinderGeometry(3.9,4.02,.12,80),new THREE.MeshStandardMaterial({color:'#17211e',metalness:.32,roughness:.68}));platform.position.y=-.12;scene.add(platform);
  const rim=new THREE.Mesh(new THREE.TorusGeometry(3.96,.014,8,96),glow);rim.rotation.x=Math.PI/2;rim.position.y=-.06;scene.add(rim);
  scene.add(new THREE.HemisphereLight('#eee8d2','#302617',2.4));
  const key=new THREE.DirectionalLight('#faf0db',4);key.position.set(-4,7,5);scene.add(key);
  const fill=new THREE.PointLight('#ffac43',65,20);fill.position.set(4,3,-3);scene.add(fill);
  const edge=new THREE.DirectionalLight(mode==='apps-market' ? '#f5ae59' : mode.startsWith('apps') ? '#5eead4' : '#a1c3bb', mode.startsWith('apps') ? 3.5 : 2.5);edge.position.set(-4,3,-5);scene.add(edge);

  // A second fine orbit adds depth without a broad reflective pedestal.
  const orbit = new THREE.Mesh(new THREE.TorusGeometry(3.55, .008, 6, 96), gold);
  orbit.rotation.x = Math.PI / 2; orbit.position.y = -.035; scene.add(orbit);

  let visible = true, disposed = false, lost = false, raf = 0;
  let animTime = 0, lastTimestamp = 0;
  let baseRotY = -.18;
  const scrollParallax = { y: 0, x: 0, camX: 0, camY: 0, camZ: 0 };
  let mouseTargetX = 0, mouseTargetY = 0;
  let mouseCurrentX = 0, mouseCurrentY = 0;

  const isMotionOff = () => matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.dataset.motion === 'paused';

  const onPointerMove = (e) => {
    if (isMotionOff()) return;
    const rect = host.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseTargetX = x * 0.35;
    mouseTargetY = -y * 0.22;
  };
  if (mode.startsWith('apps')) {
    host.addEventListener('pointermove', onPointerMove, { passive: true });
  }

  const drawStationary = () => {
    if (disposed || lost || !visible) return;
    group.rotation.y = baseRotY + mouseCurrentX + scrollParallax.y;
    group.rotation.x = mouseCurrentY + scrollParallax.x;
    camera.position.set(5.4 + scrollParallax.camX, 4.7 + scrollParallax.camY, 11.8 + scrollParallax.camZ);
    camera.lookAt(target);
    renderer.render(scene, camera);
    host.dataset.rendered = 'true';
  };

  const loop = (timestamp) => {
    if (disposed || lost || !visible || document.hidden) {
      raf = 0;
      return;
    }
    if (isMotionOff()) {
      drawStationary();
      raf = 0;
      return;
    }
    if (!lastTimestamp) lastTimestamp = timestamp;
    const dt = Math.min((timestamp - lastTimestamp) / 1000, 0.1);
    lastTimestamp = timestamp;
    animTime += dt;

    mouseCurrentX += (mouseTargetX - mouseCurrentX) * 0.08;
    mouseCurrentY += (mouseTargetY - mouseCurrentY) * 0.08;

    if (updateContinuous) {
      updateContinuous(dt, animTime);
    } else {
      baseRotY += dt * 0.04;
    }

    group.rotation.y = baseRotY + mouseCurrentX + scrollParallax.y;
    group.rotation.x = mouseCurrentY + scrollParallax.x;
    camera.position.set(5.4 + scrollParallax.camX, 4.7 + scrollParallax.camY, 11.8 + scrollParallax.camZ);

    camera.lookAt(target);
    renderer.render(scene, camera);
    host.dataset.rendered = 'true';

    raf = requestAnimationFrame(loop);
  };

  const startLoop = () => {
    if (disposed || lost || !visible || document.hidden || isMotionOff()) {
      drawStationary();
      return;
    }
    if (!raf) {
      lastTimestamp = 0;
      raf = requestAnimationFrame(loop);
    }
  };

  const stopLoop = () => {
    if (raf) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  };

  const resize = () => {
    const { width, height } = host.getBoundingClientRect();
    if (!width || !height) return;
    renderer.setSize(width, height);
    camera.aspect = width / height;
    camera.zoom = Math.min(1, camera.aspect / 1.18);
    camera.updateProjectionMatrix();
    if (isMotionOff()) drawStationary();
  };
  const ro = new ResizeObserver(resize); ro.observe(host);

  const io = new IntersectionObserver(entries => {
    visible = entries[0].isIntersecting;
    if (visible) startLoop();
    else stopLoop();
  }, { rootMargin: '100px' }); io.observe(host);

  const motionObserver = new MutationObserver(() => {
    if (isMotionOff()) {
      stopLoop();
      drawStationary();
    } else {
      startLoop();
    }
  });
  motionObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-motion'] });

  const journey = host.closest('.journey');
  const trigger = gsap.timeline({
    scrollTrigger: {
      trigger: journey || host.closest('section') || host,
      start: journey ? 'top 60%' : 'top top',
      end: journey ? 'bottom 80%' : 'bottom top',
      scrub: .65,
    },
    onUpdate: () => {
      if (isMotionOff()) drawStationary();
    },
  });

  if (journey) {
    trigger.to(scrollParallax, { y: .46, camX: -3.1, camY: -.6, camZ: .6, duration: 1, ease: 'sine.inOut' }, 0)
      .to(scrollParallax, { y: -.1, camX: -1.2, camY: .7, camZ: 0, duration: 1, ease: 'sine.inOut' }, 1);
    displays.forEach((device, i) => {
      const origin = device.position.clone();
      trigger.to(device.position, {
        y: origin.y + (i === 2 ? .42 : .08),
        z: origin.z + (i === 2 ? .5 : -.1), duration: 1, ease: 'sine.inOut',
      }, 0).to(device.position, {
        y: origin.y + (i >= 3 ? .28 : 0),
        z: origin.z + (i >= 3 ? .6 : 0), duration: 1, ease: 'sine.inOut',
      }, 1);
    });
  } else if (mode.startsWith('apps')) {
    trigger.to(scrollParallax, { y: .38, x: -.05, camZ: .35, duration: 1, ease: 'none' }, 0);
    displays.forEach((device, i) => trigger.to(device.position, {
      y: device.position.y + (i % 2 === 0 ? .22 : -.12),
      z: device.position.z + (i < 2 ? .35 : -.25),
      duration: 1, ease: 'none',
    }, 0));
  } else {
    trigger.to(scrollParallax, { y: .35, camX: -1.8, camY: .6, duration: 1, ease: 'none' }, 0);
    displays.forEach((device, i) => trigger.to(device.position, {
      y: device.position.y + (i > 1 ? .18 : .04),
      z: device.position.z + (i % 2 ? .2 : -.1), duration: 1, ease: 'none',
    }, 0));
  }

  const onVisible = () => {
    if (document.hidden) stopLoop();
    else startLoop();
  };
  const onLost = event => {
    event.preventDefault(); lost = true;
    stopLoop();
    host.removeAttribute('data-rendered'); host.dataset.fallback = 'webgl-context-lost';
  };
  const onRestored = () => {
    lost = false; delete host.dataset.fallback;
    startLoop();
  };
  document.addEventListener('visibilitychange', onVisible);
  renderer.domElement.addEventListener('webglcontextlost', onLost);
  renderer.domElement.addEventListener('webglcontextrestored', onRestored);

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    stopLoop();
    ro.disconnect();
    io.disconnect();
    motionObserver.disconnect();
    if (mode.startsWith('apps')) {
      host.removeEventListener('pointermove', onPointerMove);
    }
    trigger.scrollTrigger?.kill(); trigger.kill();
    document.removeEventListener('visibilitychange', onVisible);
    renderer.domElement.removeEventListener('webglcontextlost', onLost);
    renderer.domElement.removeEventListener('webglcontextrestored', onRestored);
    window.removeEventListener('pagehide', dispose);
    scene.traverse(object => {
      object.geometry?.dispose();
      for (const material of (Array.isArray(object.material) ? object.material : [object.material])) material?.dispose();
    });
    textures.forEach(texture => texture.dispose());
    renderer.dispose(); renderer.domElement.remove(); host.removeAttribute('data-rendered');
  };
  window.addEventListener('pagehide', dispose, { once: true });
  resize(); startLoop();
  return dispose;
}
